import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  CircleCheck,
  CreditCard,
  DotsVertical,
  Download,
  InfoCircle,
  Receipt,
  Send,
  X
} from 'tabler-icons-react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Divider,
  Menu
} from '@mantine/core';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useMediaQueryIndex, useModalState } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import {
  currencyFormat,
  formatUtcDate,
  singularPluralFormat
} from '../../../helpers/format';
import {
  ECOM_ORDER_PRODUCT_STATUS_ENUM,
  ECOM_ORDER_STATUS_ENUM,
  PAYMENT_TYPE_ENUM,
  REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { translateStoreOrderData } from '../../../helpers/storeHelper';
import MenuContent from '../../common/MenuContent';
import AppCard from '../../common/AppCard';
import OrderProductsInfoCard from './OrderProductsInfoCard';
import OrderPaymentInfoCard from './OrderPaymentInfoCard';
import OrderTrackingInfoCard from './OrderTrackingInfoCard';
import OrderCustomerInfoCard from './OrderCustomerInfoCard';
import EcomOrderTransactionModal from './EcomOrderTransactionModal';
import PageView from '../../common/PageView';

const StoreOrderDetailsView = ({
  loading,
  pkRegCart,
  ecomOrder,
  onAction,
  baseUrl,
  backPath
}) => {
  const { isTabletOrSmaller, isDesktopOrSmaller } = useMediaQueryIndex();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { search } = useLocation();
  const modalAction = new URLSearchParams(search).get('modal');
  const {
    ecomOrderProducts: productData,
    ecomOrderProductBulkChanges
  } = translateStoreOrderData(ecomOrder);

  const regularProducts = productData.filter(
    (f) => !f.isBulkShipment && f.returnedCount + f.cancelledCount < f.quantity
  );
  const groupOrderProducts = productData.filter(
    (f) => f.isBulkShipment && f.returnedCount + f.cancelledCount < f.quantity
  );
  const returnedProducts = productData.filter((f) => f.cancelledCount > 0);

  const totalUnitCost = productData.reduce((acc, p) => acc + p.totalCost, 0);
  const totalProductTax = productData.reduce((acc, p) => acc + p.taxTotal, 0);
  const totalRegularProductUnitCost =
    regularProducts.reduce((acc, p) => acc + p.totalCost - p.taxTotal, 0) ?? 0;
  const totalBulkProductUnitCost =
    groupOrderProducts.reduce((acc, p) => acc + p.totalCost - p.taxTotal, 0) ??
    0;

  const vendorPayout = ecomOrder
    ? productData.reduce((r, c) => r + c.vendorCost, 0) +
      ecomOrder.checkoutBalance.shippingTotalInCents
    : 0;
  const expectedVsRecordedServiceFeeDifference = ecomOrder
    ? ecomOrder.checkoutBalance.serviceFeeRecordedInCents -
      ecomOrder.checkoutBalance.baseServiceFeeInCents
    : 0;
  const totalFees = ecomOrder
    ? productData.reduce((r, c) => r + c.serviceFees, 0) +
      ecomOrder.checkoutBalance.tieredPricingFeeInCents +
      expectedVsRecordedServiceFeeDifference
    : 0;

  const { payments = [], reimbursement = [], refunds = [] } =
    ecomOrder?.ecomOrderPayments
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .reduce(
        (r, c) => {
          if (
            c.fkRegFormSubmissionPaymentType.toString() ===
            PAYMENT_TYPE_ENUM.REIMBURSEMENT.toString()
          ) {
            r.reimbursement.push(c);
          }
          else if (
            c.fkRegFormSubmissionPaymentType.toString() ===
            PAYMENT_TYPE_ENUM.REFUND.toString()
          ) {
            r.refunds.push(c);
          }
          else if (
            c.fkRegFormSubmissionPaymentType.toString() !==
            PAYMENT_TYPE_ENUM.CREDIT.toString()
          ) {
            r.payments.push(c);
          }
          return r;
        },
        {
          payments: [],
          reimbursement: [],
          refunds: []
        }
      ) || {};

  const taxes = productData.reduce((r, c) => {
    c.tax.forEach((t) => {
      const existingTax = r.find(
        (f) => f.name === t.name && f.taxRate === t.taxRate
      );
      if (existingTax) {
        existingTax.taxInCents += t.taxInCents * c.quantity;
      }
      else {
        r.push({
          name: t.name,
          taxRate: t.taxRate,
          taxInCents: t.taxInCents * c.quantity
        });
      }
    });
    return r;
  }, []);

  const hasCustomizedPrintImages = ecomOrder?.ecomOrderProducts.some(
    (p) => p.printImages && p.printImages.length > 0
  );

  const paidAmount = payments.reduce((r, c) => r + c.amount, 0);
  const refundAmount = refunds.reduce((r, c) => r + c.amount, 0);
  const reimbursementAmount = reimbursement.reduce((r, c) => r + c.amount, 0);

  const isFullyCancelled =
    productData.length > 0 &&
    productData.every((p) => p.cancelledCount >= p.quantity);
  const isFullyRefunded = ecomOrder?.isBulkOrders
    ? productData.some(
        (p) =>
          p.fkEcomOrderProductStatus.toString() !==
          ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING
      )
    : refundAmount >= paidAmount;

  const firstPayment = ecomOrder?.ecomOrderPayments.sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )[0];
  const accountPayee = firstPayment?.ecomStorePaymentProvider?.accountId;
  const isEscrowPayment = !accountPayee;

  const baseStoreProfit = ecomOrder
    ? (ecomOrder.checkoutBalance.totalInCents || totalUnitCost) -
      vendorPayout -
      totalFees -
      ecomOrder.checkoutBalance.taxTotalInCents
    : 0;
  const avaialbleBalance = ecomOrder
    ? (ecomOrder.checkoutBalance.totalInCents || totalUnitCost) -
      vendorPayout -
      refundAmount -
      totalFees +
      reimbursementAmount
    : 0;

  const isCompletedOffline =
    ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.COMPLETED_OFFLINE.toString();
  const isAccumulating =
    ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.ACCUMULATING.toString();
  ecomOrder?.fkEcomOrderStatus.toString() ===
    ECOM_ORDER_STATUS_ENUM.ACCUMULATING.toString();
  const isPendingFulfillment = ecomOrder?.isBulkShipment
    ? isAccumulating
    : productData.some(
        (p) =>
          p.fkEcomOrderProductStatus.toString() ===
          ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING
      );

  useEffect(() => {
    if (modalAction) {
      switch (modalAction) {
        case 'cancel':
          onOpenModal('cancel_order');
          break;
        case 'complete_offline':
          onOpenModal('complete_offline');
          break;
        case 'submit_order':
          onOpenModal('submit_order');
          break;
        default:
          break;
      }
    }
  }, [modalAction]);

  const renderStatusBadges = () => (
    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
      {isFullyCancelled ? (
        <Badge color="#C40000" variant="filled">
          Cancelled
        </Badge>
      ) : isCompletedOffline ? (
        groupOrderProducts.length === 0 ? (
          <Badge color="green" variant="filled">
            Transferred
          </Badge>
        ) : (
          <Badge color="green" variant="filled">
            Completed offline
          </Badge>
        )
      ) : ecomOrder?.fkEcomOrderStatus.toString() ===
        ECOM_ORDER_STATUS_ENUM.ACCUMULATING.toString() ? (
        <Badge color="blue" variant="filled">
          Accumulating
        </Badge>
      ) : (
          ecomOrder.isBulkShipment
            ? groupOrderProducts.every(
                (p) =>
                  p.fkEcomOrderProductStatus?.toString() !==
                    ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING &&
                  p.fkEcomOrderProductStatus?.toString() !==
                    ECOM_ORDER_PRODUCT_STATUS_ENUM.ACCEPTED
              )
            : regularProducts.every(
                (p) =>
                  p.fkEcomOrderProductStatus?.toString() !==
                    ECOM_ORDER_PRODUCT_STATUS_ENUM.PENDING &&
                  p.fkEcomOrderProductStatus?.toString() !==
                    ECOM_ORDER_PRODUCT_STATUS_ENUM.ACCEPTED
              )
        ) ? (
        <Badge color="green" variant="filled">
          Fulfilled
        </Badge>
      ) : (
        <Badge color="dark" variant="light">
          Unfulfilled
        </Badge>
      )}
      {refunds.length > 0 ? (
        refundAmount >= paidAmount ? (
          <Badge color="#C40000" variant="filled">
            Refunded
          </Badge>
        ) : (
          <Badge color="grey" variant="filled">
            Partially refunded
          </Badge>
        )
      ) : (
        !ecomOrder.isBulkShipment && (
          <Badge color="blue" variant="filled">
            Paid
          </Badge>
        )
      )}
      {!ecomOrder?.isBulkShipment && isEscrowPayment && (
        <Badge color="blue" variant="filled">
          Escrow
        </Badge>
      )}
    </AppFlexbox>
  );

  return !loading && ecomOrder ? (
    <PageView
      backPath="/admin/financials/merchandise"
      badgeContent={renderStatusBadges()}
      buttonAction={
        <MenuContent
          control={
            <Button
              color="dark"
              rightSection={<ChevronDown size={18} />}
              size="compact-md"
              style={{ fontSize: 14, flex: 1 }}
              variant="light"
            >
              Actions
            </Button>
          }
        >
          <Menu.Item
            leftSection={<Download size={18} />}
            onClick={() => onAction('export_shop_order')}
          >
            Export order
          </Menu.Item>
          <Menu.Item
            leftSection={<Receipt size={18} />}
            onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.VIEW)}
          >
            View transactions
          </Menu.Item>
          <Menu.Item
            leftSection={<Send size={18} />}
            onClick={() =>
              onAction(REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.resendReceipt)
            }
          >
            Resend Receipt
          </Menu.Item>
          {/* {(!isFullyRefunded ||
            (!isFullyCancelled && isPendingFulfillment)) && (
            <>
              <Menu.Divider />
              {!isFullyCancelled && isPendingFulfillment && (
                <Menu.Item
                  leftSection={<X color="#c40000" size={18} />}
                  onClick={() => onOpenModal('cancel_order')}
                  style={{ color: '#c40000' }}
                >
                  Cancel order
                </Menu.Item>
              )}
              {!isFullyRefunded && (
                <Menu.Item
                  component={Link}
                  leftSection={<CreditCard color="#c40000" size={18} />}
                  style={{ color: '#c40000' }}
                  to={`/merchant/orders/${ecomOrder.pkEcomOrder}/refund`}
                >
                  Refund order
                </Menu.Item>
              )}
            </>
          )} */}
        </MenuContent>
      }
      subTitle={`Placed
                    ${dayjs(formatUtcDate(ecomOrder.purchaseDate)).format(
                      'MMMM D, YYYY'
                    )}
                    at
                    ${dayjs(formatUtcDate(ecomOrder.purchaseDate)).format(
                      'h:mm A'
                    )}`}
      title={`Shop Order #${ecomOrder.pkEcomOrder}`}
    >
      {isCompletedOffline && (
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 0 }}
          withBorder
        >
          <AppStack style={{ gap: 0 }}>
            <AppFlexbox style={{ padding: 8, backgroundColor: '#067D62' }}>
              <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                <CircleCheck color="#FFF" size={18} />
                <AppText
                  style={{ fontSize: 14, fontWeight: 500, color: '#FFF' }}
                >
                  {groupOrderProducts.length === 0
                    ? 'Transferred'
                    : 'Completed offline'}
                </AppText>
              </AppFlexbox>
            </AppFlexbox>

            <Divider />
            <AppFlexbox style={{ padding: 8 }}>
              <AppText
                style={{
                  color: '#000',
                  fontSize: 14
                }}
              >
                This order has been{' '}
                {groupOrderProducts.length === 0
                  ? 'transferred'
                  : 'completed offline'}
                . The vendor will not have visibility of the order in their
                system.
              </AppText>
            </AppFlexbox>
          </AppStack>
        </AppCard>
      )}

      <AppCard
        radius={isTabletOrSmaller ? 0 : 'md'}
        shadow="xs"
        style={{ padding: 0 }}
        withBorder
      >
        <AppStack style={{ gap: 0 }}>
          <AppFlexbox style={{ padding: 8, backgroundColor: 'dodgerblue' }}>
            <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
              <InfoCircle color="#FFF" size={18} />
              <AppText style={{ fontSize: 14, fontWeight: 500, color: '#FFF' }}>
                Notice
              </AppText>
            </AppFlexbox>
          </AppFlexbox>

          <Divider />
          <AppFlexbox style={{ padding: 8 }}>
            <AppText
              style={{
                color: '#000',
                fontSize: 14
              }}
            >
              You are currently viewing the shop order. To access the full
              registration order, please{' '}
              <Anchor
                color="dodgerblue"
                component={Link}
                style={{ fontSize: 14 }}
                to={`${baseUrl}/${pkRegCart}?from=merchandise&fromId=${ecomOrder.pkEcomOrder}`}
              >
                click here
              </Anchor>
              .
            </AppText>
          </AppFlexbox>
        </AppStack>
      </AppCard>

      <AppFlexbox
        style={{ flexDirection: isDesktopOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <>
            <OrderProductsInfoCard
              // actionButton={
              //   isPendingFulfillment &&
              //   !isFullyCancelled &&
              //   regularProducts.some((p) => p.amountRemaining > 0) && (
              //     <MenuContent
              //       control={
              //         <ActionIcon color="dark" variant="subtle">
              //           <DotsVertical />
              //         </ActionIcon>
              //       }
              //     >
              //       <Menu.Item
              //         leftSection={<X color="#c40000" size={18} />}
              //         onClick={() => onOpenModal('cancel_products')}
              //         style={{ color: '#c40000' }}
              //       >
              //         Cancel products
              //       </Menu.Item>
              //     </MenuContent>
              //   )
              // }
              disableNavigation
              isCancelled={isFullyCancelled}
              productData={regularProducts}
              showVendorName
              title={ecomOrder.isBulkShipment ? 'Variants' : 'Products'}
            />

            {groupOrderProducts.length > 0 && (
              <OrderProductsInfoCard
                // actionButton={
                //   !isFullyCancelled &&
                //   groupOrderProducts.some((p) => p.amountRemaining > 0) && (
                //     <MenuContent
                //       control={
                //         <ActionIcon color="dark" variant="subtle">
                //           <DotsVertical />
                //         </ActionIcon>
                //       }
                //     >
                //       <Menu.Item
                //         leftSection={<X color="#c40000" size={18} />}
                //         onClick={() => onOpenModal('cancel_products')}
                //         style={{ color: '#c40000' }}
                //       >
                //         Cancel products
                //       </Menu.Item>
                //     </MenuContent>
                //   )
                // }
                description="These products are grouped with items from other orders and will be fulfilled separately."
                disableNavigation
                productData={groupOrderProducts}
                showVendorName
                title="Grouped products"
              />
            )}
          </>

          <OrderPaymentInfoCard
            actionMenu={
              !ecomOrder.isBulkShipment && (
                <MenuContent
                  control={
                    <ActionIcon color="dark" variant="subtle">
                      <DotsVertical />
                    </ActionIcon>
                  }
                >
                  <Menu.Item
                    leftSection={<Receipt size={18} />}
                    onClick={() => {
                      onOpenModal(VIEW_ACTIONS_ENUM.VIEW);
                    }}
                  >
                    View transactions
                  </Menu.Item>
                  {/* {(!isFullyRefunded || !isFullyCancelled) && (
                    <>
                      <Menu.Divider />
                      {!isFullyCancelled && isPendingFulfillment && (
                        <Menu.Item
                          leftSection={<X color="#c40000" size={18} />}
                          onClick={() => onOpenModal('cancel_order')}
                          style={{ color: '#c40000' }}
                        >
                          Cancel order
                        </Menu.Item>
                      )}
                      {!isFullyRefunded && (
                        <Menu.Item
                          component={Link}
                          leftSection={<CreditCard color="#c40000" size={18} />}
                          style={{ color: '#c40000' }}
                          to={`/merchant/orders/${ecomOrder.pkEcomOrder}/refund`}
                        >
                          Refund order
                        </Menu.Item>
                      )}
                    </>
                  )} */}
                </MenuContent>
              )
            }
            payments={[
              ...(groupOrderProducts.length > 0 && regularProducts.length > 0
                ? [
                    {
                      key: 1,
                      group: 1,
                      label: 'Regular products',
                      color: '#666',
                      description: singularPluralFormat(
                        regularProducts.reduce((r, c) => r + c.quantity, 0),
                        'item',
                        'items'
                      ),
                      value: totalRegularProductUnitCost / 100
                    },
                    {
                      key: 2,
                      group: 1,
                      label: 'Grouped products',
                      color: '#666',
                      description: singularPluralFormat(
                        groupOrderProducts.reduce((r, c) => r + c.quantity, 0),
                        'item',
                        'items'
                      ),
                      value: totalBulkProductUnitCost / 100
                    }
                  ]
                : [
                    {
                      key: 1,
                      group: 1,
                      label: 'Subtotal',
                      color: '#666',
                      description: singularPluralFormat(
                        productData.reduce(
                          (acc, item) => acc + item.quantity,
                          0
                        ),
                        'item',
                        'items'
                      ),
                      value: (totalUnitCost - totalProductTax) / 100
                    }
                  ]),
              ...(ecomOrder.checkoutBalance.taxTotalInCents > 0
                ? [
                    {
                      key: 3,
                      group: 1,
                      label: 'Taxes',
                      color: '#666',
                      description: 'Tax details',
                      value: ecomOrder.checkoutBalance.taxTotalInCents / 100,
                      children: [
                        ...taxes.map((t) => ({
                          key: `${t.name}-${t.taxRate}`,
                          label: `${t.name} (${t.taxRate}%)`,
                          rate: t.taxRate.toString(),
                          color: '#666',
                          value: t.taxInCents / 100
                        })),
                        {
                          key: '',
                          rate:
                            ecomOrder.checkoutBalance.taxRates.length > 1
                              ? 'Mixed'
                              : (
                                  ecomOrder.checkoutBalance.taxRates[0] * 100
                                ).toString(),
                          color: '#666',
                          value:
                            (ecomOrder.checkoutBalance.taxTotalInCents -
                              totalProductTax) /
                            100
                        }
                      ].reduce((r, c) => {
                        const exsiting = r.find(
                          (f) =>
                            f.rate === c.rate &&
                            (!c.label || r.label === c.label)
                        );
                        if (!exsiting) {
                          r.push(c);
                        }
                        else {
                          exsiting.value += c.value;
                        }
                        return r;
                      }, [])
                    }
                  ]
                : []),
              ...(ecomOrder.checkoutBalance.shippingTotalInCents > 0
                ? [
                    {
                      key: 5,
                      group: 1,
                      label: 'Shipping',
                      color: '#666',
                      description: 'Shipping details',
                      value:
                        ecomOrder.checkoutBalance.shippingTotalInCents / 100,
                      children: ecomOrder.checkoutBalance.shippingCosts.map(
                        (t) => ({
                          key: t.ecomExternalConnectionExternalId,
                          label: t.label,
                          description: t.vendorName,
                          color: '#666',
                          value: t.totalInCents / 100
                        })
                      )
                    }
                  ]
                : []),
              ...(ecomOrder.checkoutBalance.tieredPricingFeeInCents > 0
                ? [
                    {
                      key: 6,
                      group: 1,
                      label: 'Platform fee',
                      color: '#666',
                      description: '-',
                      value:
                        ecomOrder.checkoutBalance.tieredPricingFeeInCents / 100
                    }
                  ]
                : []),
              {
                key: 7,
                group: 2,
                label: 'Total',
                weight: 500,
                description: '',
                value:
                  (ecomOrder.checkoutBalance.totalInCents || totalUnitCost) /
                  100
              },
              {
                key: 8,
                group: 2,
                label: 'Vendor payout',
                color: '#666',
                value: (vendorPayout / 100) * -1
              },
              {
                key: 9,
                group: 2,
                label:
                  ecomOrder.checkoutBalance.taxTotalInCents > 0
                    ? 'Fees & taxes'
                    : 'Fees',
                color: '#666',
                value:
                  ((totalFees + ecomOrder.checkoutBalance.taxTotalInCents) /
                    100) *
                  -1,
                tooltipIcon: <InfoCircle color="#C40000" size={18} />,
                tooltip: expectedVsRecordedServiceFeeDifference !== 0 && (
                  <AppStack style={{ gap: 8 }}>
                    <AppText style={{ fontSize: 14 }}>
                      Recorded service fee{' '}
                      <b>
                        {currencyFormat(
                          expectedVsRecordedServiceFeeDifference / 100
                        )}{' '}
                        {expectedVsRecordedServiceFeeDifference < 0
                          ? 'lower'
                          : 'higher'}
                      </b>{' '}
                      than anticipated.
                    </AppText>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Click for more info.
                    </AppText>
                  </AppStack>
                ),
                infoTitle: 'Service fee discrepancy',
                infoContent:
                  expectedVsRecordedServiceFeeDifference !== 0 ? (
                    <AppStack style={{ gap: 8 }}>
                      <AppText style={{ fontSize: 14 }}>
                        Recorded service fee{' '}
                        <b>
                          {currencyFormat(
                            expectedVsRecordedServiceFeeDifference / 100
                          )}{' '}
                          {expectedVsRecordedServiceFeeDifference < 0
                            ? 'lower'
                            : 'higher'}
                        </b>{' '}
                        than anticipated.
                      </AppText>
                      <AppText style={{ fontSize: 14 }}>
                        This discrepancy may be due to additional service fees
                        applied by Stripe, such as a{' '}
                        <b>+0.8% fee for international cards</b>.
                      </AppText>
                      <AppText style={{ fontSize: 14 }}>
                        For more details, please visit{' '}
                        <Anchor
                          component="a"
                          href="https://stripe.com/en-ca/pricing"
                          style={{ fontSize: 14, color: 'dodgerblue' }}
                          target="_blank"
                        >
                          Stripe pricing
                        </Anchor>{' '}
                        or contact support if you have any questions.
                      </AppText>

                      <AppText
                        style={{
                          marginTop: 8,
                          fontSize: 14,
                          fontStyle: 'italic',
                          fontWeight: 500,
                          color: '#666',
                          textAlign: 'center'
                        }}
                      >
                        This variance will directly impact the store's profit
                        margin.
                      </AppText>
                    </AppStack>
                  ) : null
              },
              ...(refunds.length > 0 || reimbursementAmount > 0
                ? [
                    {
                      key: 10,
                      group: 3,
                      label: 'Received',
                      weight: 500,
                      description: '',
                      value: baseStoreProfit / 100
                    },
                    {
                      key: 11,
                      group: 3,
                      label: 'Reimbursed',
                      prefix: reimbursementAmount > 0 ? '+$' : '$',
                      color: '#666',
                      valueColor: reimbursementAmount > 0 ? '#067D62' : '#666',
                      description: '',
                      value: reimbursementAmount / 100
                    },
                    {
                      key: 12,
                      group: 3,
                      label: 'Refunded',
                      color: '#c40000',
                      description: '',
                      value: (refundAmount * -1) / 100
                    }
                  ]
                : []),
              {
                key: 11,
                group: 4,
                label: 'Profit',
                weight: 500,
                description: '',
                tooltipIcon: <InfoCircle color="dodgerblue" size={18} />,
                tooltip: isEscrowPayment &&
                  avaialbleBalance - ecomOrder.checkoutBalance.taxTotalInCents >
                    baseStoreProfit && (
                    <AppStack style={{ gap: 8 }}>
                      <AppText style={{ fontSize: 14 }}>
                        You cannot claim more than the amount received from
                        reimbursements for escrow payments.
                      </AppText>
                    </AppStack>
                  ),
                value:
                  (isEscrowPayment
                    ? Math.min(baseStoreProfit, avaialbleBalance)
                    : avaialbleBalance -
                      ecomOrder.checkoutBalance.taxTotalInCents) / 100
              }
            ]}
          />

          <OrderTrackingInfoCard
            ecomOrderShipping={ecomOrder.ecomOrderShipping}
            loading={loading}
            productData={productData}
          />

          {returnedProducts.length > 0 && (
            <OrderProductsInfoCard
              description="Cancelled product will not be fulfilled."
              disableNavigation
              hideStatus
              onViewTransactions={() => onOpenModal(VIEW_ACTIONS_ENUM.VIEW)}
              productData={returnedProducts}
              showReimbursement
              showVendorName
              title="Cancelled products"
            />
          )}

          {ecomOrderProductBulkChanges.transferred.length > 0 && (
            <OrderProductsInfoCard
              description="Transferred products will be fulfilled and managed in a different order."
              disableNavigation
              hideStatus
              productData={ecomOrderProductBulkChanges.transferred}
              showTransferInfo
              showVendorName
              title="Transferred products"
            />
          )}

          {ecomOrderProductBulkChanges.cancelled.length > 0 && (
            <OrderProductsInfoCard
              description="Cancelled product will not be fulfilled."
              disableNavigation
              hideStatus
              productData={ecomOrderProductBulkChanges.cancelled}
              showParentOrder={ecomOrder.isBulkShipment}
              showVendorName
              title="Cancelled products"
            />
          )}
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isDesktopOrSmaller ? 'unset' : 350 }}
        >
          <OrderCustomerInfoCard
            billingAddress={
              payments.find((p) => !!p.ecomCustomerAddressBilling)
                ?.ecomCustomerAddressBilling
            }
            customerEmail={ecomOrder.ecomCustomer.email}
            customerName={ecomOrder.ecomCustomer.user?.name}
            isBulkOrders={ecomOrder.isBulkShipment}
            shippingAddress={ecomOrder.ecomCustomerAddressShipping}
          />
        </AppStack>
      </AppFlexbox>

      <EcomOrderTransactionModal
        ecomOrder={ecomOrder}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.VIEW
        }
        onClose={onCloseModal}
      />

      {/* <OrderCancelModal
          ecomOrder={ecomOrder}
          isOpen={
            modalState.isOpen &&
            (modalState.action === 'cancel_order' ||
              modalState.action === 'cancel_products')
          }
          isProductCancel={modalState.action === 'cancel_products'}
          onClose={onCloseModal}
        /> */}
    </PageView>
  ) : (
    <PageView loading>
      <AppFlexbox
        style={{ flexDirection: isDesktopOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <OrderProductsInfoCard loading />
          <OrderPaymentInfoCard loading />
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isDesktopOrSmaller ? 'unset' : 350 }}
        >
          <OrderCustomerInfoCard loading />
        </AppStack>
      </AppFlexbox>
    </PageView>
  );
};

StoreOrderDetailsView.propTypes = {
  backPath: PropTypes.string,
  baseUrl: PropTypes.string,
  ecomOrder: PropTypes.object,
  isBulkOrders: PropTypes.bool,
  loading: PropTypes.bool,
  onAction: PropTypes.func,
  pkRegCart: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default StoreOrderDetailsView;
